import DOMPurify from "dompurify";
import TurndownService from "turndown";

const createTurndownService = () => {
  const turndownService = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
    strongDelimiter: "**",
    linkStyle: "inlined",
    linkReferenceStyle: "full",
  });

  // Add custom rules for dimension tags and person mentions
  turndownService.addRule("dimensionTag", {
    filter: (node, options) => {
      return (
        node.nodeName === "SPAN" && (node as Element).hasAttribute("dim_id")
      );
    },
    replacement: (content, node, options) => {
      const dimId = (node as Element).getAttribute("dim_id");
      return `[DIMENSION:${dimId}:${content}]`;
    },
  });

  turndownService.addRule("personMention", {
    filter: (node, options) => {
      return (
        node.nodeName === "SPAN" && (node as Element).hasAttribute("person_id")
      );
    },
    replacement: (content, node, options) => {
      const personId = (node as Element).getAttribute("person_id");
      return `[PERSON:${personId}:${content}]`;
    },
  });

  // Ensure strong and b tags are properly converted to **bold**
  turndownService.addRule("strongBold", {
    filter: ["strong", "b"],
    replacement: (content, node, options) => {
      return `**${content}**`;
    },
  });

  return turndownService;
};

export const htmlToMarkdown = (text: string): string => {
  try {
    const htmlTagRegex = /<[^>]*>/;

    if (!htmlTagRegex.test(text)) {
      return text;
    }

    // Check if the content is already markdown (contains ** or * patterns)
    const markdownPattern = /\*\*[^*]+\*\*|\*[^*]+\*/;
    const hasMarkdownPatterns = markdownPattern.test(text);

    // Pre-process text if it contains markdown patterns
    const processedText = hasMarkdownPatterns
      ? text.replace(/\\\*/g, "*")
      : text;
    const sanitized = DOMPurify.sanitize(processedText);

    // Create turndown service and convert
    const turndownService = createTurndownService();
    const result = turndownService.turndown(sanitized);

    // Post-process to fix any escaped markdown patterns if needed
    return hasMarkdownPatterns ? result.replace(/\\\*/g, "*") : result;
  } catch (error) {
    return "";
  }
};
